import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    Link,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import TaskTrackingService from '../../services/TaskTrackingService';
import { weekdayDay } from './calendarUi';
import { BLOCK_REASONS, formatTime, handoffLines, reasonLabel, SEND_BACK_REASONS, shortName } from './taskUi';

/** The boxed card every step uses, so there's one place to look. */
export const StepBox = ({ heading = 'Your step', title, line, children }) => (
    <Box sx={{ mt: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        {heading && (
            <Typography variant="overline" component="div" color="text.secondary">
                {heading}
            </Typography>
        )}
        {title && (
            <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600 }}>
                {title}
            </Typography>
        )}
        {line && (
            <Typography variant="body2" color="text.secondary">
                {line}
            </Typography>
        )}
        {children}
    </Box>
);

/** The newest send-back to this stage, which is why it's open again. Posts show theirs by the boxes. */
export const sentBackEvent = (events, stageKey) =>
    [...events].reverse().find((e) => e.action === 'SENT_BACK' && e.stageKey === stageKey) || null;

/** The person on the step: why it came back, then what Complete does. The buttons are pinned below it. */
const TaskStepCard = ({ task, detail, perms, current, linkedLevel, events, focusAction, step, openFlag }) => {
    const {
        nextStage,
        handoffLocked,
        setHandoff,
        handoffOptions,
        handoffUser,
        grantHandoffAccess,
        setGrantHandoffAccess,
        completeNote,
        setCompleteNote,
        noteOpen,
        setNoteOpen,
        choice,
        setChoice,
        completeChoices,
        sendingBackTo,
        finishing,
        sendParts,
        setSendParts,
        several,
        picked,
        completingSeveral,
        unticked,
        setUnticked,
    } = step;
    const isPost = task.contentType === 'POST';
    const back = isPost ? null : sentBackEvent(events, current.stageKey);
    return (
        <StepBox>
            {back && (
                <Alert severity="warning" icon={false} sx={{ mb: 1 }}>
                    <b>
                        Sent back by {shortName(back.actor)}, {formatTime(back.timestamp)}
                        {back.reasonCode ? ` · ${reasonLabel(SEND_BACK_REASONS, back.reasonCode)}` : ''}
                    </b>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {back.note}
                    </Typography>
                </Alert>
            )}
            <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
                {current.displayName}
            </Typography>
                {current.auto && !linkedLevel && !perms.canManage && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Completes automatically when this level is submitted in the editor.
                    </Typography>
                )}
                {perms.canComplete && (
                    <Box
                        sx={{ mb: 1 }}
                        data-focus={focusAction === 'complete' ? 'complete' : undefined}
                    >
                        {completeChoices.length > 1 && (
                            <FormControl sx={{ display: 'flex', mb: 1 }}>
                                <FormLabel
                                    id="complete-choice-label"
                                    sx={{ fontWeight: 600, color: 'text.primary' }}
                                >
                                    What's next?
                                </FormLabel>
                                <RadioGroup
                                    aria-labelledby="complete-choice-label"
                                    value={choice}
                                    onChange={(e) => setChoice(e.target.value)}
                                >
                                    {completeChoices.map((o) => (
                                        <FormControlLabel
                                            key={o.value}
                                            value={o.value}
                                            control={<Radio size="small" />}
                                            sx={{
                                                alignItems: 'flex-start',
                                                mt: 0.5,
                                                '& .MuiRadio-root': { pt: 0.25 },
                                            }}
                                            label={
                                                <>
                                                    <Typography variant="body2">{o.label}</Typography>
                                                    {o.help && (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            {o.help}
                                                        </Typography>
                                                    )}
                                                </>
                                            }
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        )}
                        {sendingBackTo && isPost && current.stageKey === 'QC' && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                                {[
                                    detail.post?.subtitleVideo ? ['SUBTITLES', 'Subtitles'] : ['DESIGN', 'Text on design'],
                                    ['CAPTION', 'Caption'],
                                ].map(([value, label]) => (
                                    <FormControlLabel
                                        key={value}
                                        label={<Typography variant="body2">{label}</Typography>}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={sendParts.includes(value)}
                                                onChange={(e) =>
                                                    setSendParts((p) =>
                                                        e.target.checked ? [...p, value] : p.filter((x) => x !== value),
                                                    )
                                                }
                                            />
                                        }
                                    />
                                ))}
                            </Box>
                        )}
                        {several.length > 1 && (
                            <Box sx={{ mb: 1 }} role="group" aria-label="Languages to complete">
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {current.displayName} is yours in {several.length} languages.
                                    {new Set(several.map((e) => e.dueDate)).size === 1 && several[0].dueDate
                                        ? ` Due ${weekdayDay(several[0].dueDate)}.`
                                        : ''}
                                </Typography>
                                {several.map((e) => (
                                    <FormControlLabel
                                        key={e.languageKey}
                                        sx={{ display: 'flex' }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={!unticked.includes(e.languageKey)}
                                                onChange={(ev) =>
                                                    setUnticked((u) =>
                                                        ev.target.checked
                                                            ? u.filter((k) => k !== e.languageKey)
                                                            : [...u, e.languageKey],
                                                    )
                                                }
                                            />
                                        }
                                        label={
                                            <Typography variant="body2">
                                                {e.languageLabel}
                                                {e.warning && (
                                                    <Typography component="span" variant="caption" color="warning.main">
                                                        {' '}
                                                        · {e.warning}
                                                    </Typography>
                                                )}
                                            </Typography>
                                        }
                                    />
                                ))}
                            </Box>
                        )}
                        {sendingBackTo ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {sendingBackTo.assignedTo
                                    ? `${shortName(sendingBackTo.assignedTo)} gets it back by email, with your comment.`
                                    : 'It goes back with your comment.'}
                            </Typography>
                        ) : finishing ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Completing this finishes the task.
                            </Typography>
                        ) : !choice ? null : completingSeveral ? (
                            nextStage && (
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {handoffLines(picked).join(' ')}
                                    {completeNote.trim() ? ' Your note goes on all of them.' : ''}
                                </Typography>
                            )
                        ) : nextStage ? (
                            <>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    Complete and hand {nextStage.displayName} to:
                                </Typography>
                                {handoffLocked ? (
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                        {shortName(nextStage.plannedAssignee)} (planned by PM)
                                    </Typography>
                                ) : (
                                    <Autocomplete
                                        size="small"
                                        options={[
                                            ...handoffOptions,
                                            { email: '__flag__', name: 'Not listed: flag for PM' },
                                        ]}
                                        value={handoffUser || null}
                                        getOptionLabel={(u) =>
                                            u.email === '__flag__' ? u.name : u.name || u.email
                                        }
                                        isOptionEqualToValue={(a, b) => a.email === b.email}
                                        onChange={(_, u) => {
                                            if (u?.email === '__flag__') {
                                                openFlag({ label: 'Who should take it next?' });
                                                return;
                                            }
                                            setHandoff(u ? u.email : null);
                                            setGrantHandoffAccess(false);
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Next person" />
                                        )}
                                        sx={{ mb: 1 }}
                                    />
                                )}
                                {perms.canManage && handoffUser && !handoffUser.canEdit && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={grantHandoffAccess}
                                                onChange={(e) =>
                                                    setGrantHandoffAccess(e.target.checked)
                                                }
                                            />
                                        }
                                        label={`Give ${shortName(handoffUser.email)} edit access to this file's folder`}
                                    />
                                )}
                            </>
                        ) : (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                This is the last stage; completing it finishes the task.
                            </Typography>
                        )}
                        {sendingBackTo || noteOpen ? (
                            <TextField
                                size="small"
                                fullWidth
                                multiline
                                minRows={2}
                                autoFocus={!sendingBackTo}
                                label={sendingBackTo ? 'What needs changing?' : 'Note (optional)'}
                                required={!!sendingBackTo}
                                value={completeNote}
                                onChange={(e) => setCompleteNote(e.target.value)}
                            />
                        ) : (
                            <Link component="button" variant="body2" onClick={() => setNoteOpen(true)}>
                                Add a note
                            </Link>
                        )}
                    </Box>
                )}
                {isPost && current.stageKey === 'QC' && (
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                        Design problem only? Use Flag for PM, and the PM sends it back to Replication.
                    </Typography>
                )}
        </StepBox>
    );
};

/** One line of small links under the main button. */
const QuietLinks = ({ intro, links }) => (
    <Typography variant="body2" color="text.secondary" component="div" sx={{ textAlign: 'center', mt: 0.5 }}>
        {intro ? `${intro} ` : ''}
        {links.map((l, i) => (
            <span key={l.label}>
                {i > 0 ? ' · ' : ''}
                <Link component="button" variant="body2" color="inherit" onClick={l.onClick}>
                    {l.label}
                </Link>
            </span>
        ))}
    </Typography>
);

/** Block dialog settings, for the trouble line. */
export const blockDialog = (task) => ({
    usageName: 'TaskBlockDialog',
    title: 'Block stage',
    description: 'Use this when you are waiting on someone else.',
    submitLabel: 'Block',
    submitColor: 'error',
    fields: [
        { name: 'reasonCode', label: 'Waiting on', type: 'select', options: BLOCK_REASONS, required: true },
        { name: 'note', label: 'Details', multiline: true },
    ],
    submit: (vals) => TaskTrackingService.block(task.id, { version: task.version, ...vals }),
});

/**
 * The row pinned to the bottom of the drawer (D131): at most one main button (or an outlined one on
 * someone else's step), one grey hint, then small links. Moving the step card alone wouldn't help, since a post's boxes can be long.
 */
export const TaskStepActions = ({ main, secondary, hint, trouble, links }) => {
    if (!main && !secondary && !hint && !trouble?.length && !links?.length) return null;
    return (
        <Box sx={{ flexShrink: 0, px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            {main && (
                <Button
                    fullWidth
                    variant="contained"
                    color={main.color}
                    startIcon={main.icon ? <CheckCircleIcon /> : undefined}
                    disabled={main.disabled}
                    onClick={main.onClick}
                >
                    {main.label}
                </Button>
            )}
            {secondary && (
                <Button fullWidth variant="outlined" onClick={secondary.onClick}>
                    {secondary.label}
                </Button>
            )}
            {hint && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, textAlign: 'center' }}>
                    {hint}
                </Typography>
            )}
            {trouble?.length > 0 && <QuietLinks intro="Something wrong?" links={trouble} />}
            {links?.length > 0 && <QuietLinks links={links} />}
        </Box>
    );
};

/** The questions Complete can ask first. They sit outside the card, so they show while it's folded. */
export const TaskCompleteDialogs = ({ task, link, linkedLevel, step }) => {
    const { softWarning, setSoftWarning, confirmEditing, setConfirmEditing, complete, completeSeveral } = step;
    return (
        <>
    <Dialog open={!!softWarning} onClose={() => setSoftWarning(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{softWarning?.title}</DialogTitle>
        <DialogContent>
            <Typography variant="body2">{softWarning?.body}</Typography>
        </DialogContent>
        <DialogActions>
            {softWarning?.other ? (
                <Button
                    onClick={() => {
                        const go = softWarning.other.onClick;
                        setSoftWarning(null);
                        go();
                    }}
                >
                    {softWarning.other.label}
                </Button>
            ) : (
                <Button onClick={() => setSoftWarning(null)}>Cancel</Button>
            )}
            <Button
                color="success"
                onClick={() => {
                    const forSeveral = softWarning?.several;
                    setSoftWarning(null);
                    if (forSeveral) completeSeveral();
                    else complete();
                }}
            >
                Complete anyway
            </Button>
        </DialogActions>
    </Dialog>

    <Dialog open={confirmEditing} onClose={() => setConfirmEditing(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
            {link?.activeEditor ? shortName(link.activeEditor) : 'Someone'} is editing {task?.languageLabel} now
        </DialogTitle>
        <DialogContent>
            <Typography variant="body2">
                Their unsaved changes won't be in the {linkedLevel} submission. Ask them to save first, or
                continue.
            </Typography>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setConfirmEditing(false)}>Wait</Button>
            <Button
                color="success"
                onClick={() => {
                    setConfirmEditing(false);
                    complete();
                }}
            >
                Complete anyway
            </Button>
        </DialogActions>
    </Dialog>
        </>
    );
};

export default TaskStepCard;
